import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Screen } from '../types';
import api from '../../../services/api';

const FaceMatchScreen = ({ userData, navigateTo }) => {
  const [step, setStep] = useState('intro'); // 'intro', 'camera', 'capturing', 'comparing', 'success', 'failed'
  const [error, setError] = useState('');
  const [similarity, setSimilarity] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Start camera
  const startCamera = async () => {
    setError('');
    setCameraReady(false);
    
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setError('Camera is not supported in this browser.');
        setStep('intro');
        return;
      }

      const isEmbedded = (() => {
        try {
          return window.self !== window.top;
        } catch (_) {
          return true;
        }
      })();

      const policyAllowsCamera = (() => {
        try {
          if (document?.permissionsPolicy?.allowsFeature) {
            return document.permissionsPolicy.allowsFeature('camera');
          }
          if (document?.featurePolicy?.allowsFeature) {
            return document.featurePolicy.allowsFeature('camera');
          }
          return true; // can't detect; assume allowed
        } catch (_) {
          return true;
        }
      })();

      if (!policyAllowsCamera) {
        setError(
          isEmbedded
            ? 'Camera is blocked by Permissions Policy (embedded page). The embedding <iframe> must include allow="camera" and must not be sandboxed in a way that blocks camera.'
            : 'Camera is blocked by Permissions Policy for this page.'
        );
        setStep('intro');
        return;
      }

      if (!window.isSecureContext) {
        setError('Camera requires a secure context (HTTPS or localhost).');
        setStep('intro');
        return;
      }

      setStep('camera');

      // Try front camera first, then fall back to any available camera.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'user' },
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
      } catch (err) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      const name = err?.name || '';
      const isEmbedded = (() => {
        try {
          return window.self !== window.top;
        } catch (_) {
          return true;
        }
      })();

      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError(
          isEmbedded
            ? 'Camera is blocked in this embedded view. Ensure the embedding <iframe> has allow="camera" and the browser permission is granted.'
            : 'Camera permission was denied. Please allow camera access and try again.'
        );
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setError('No camera device found on this device.');
      } else if (name === 'NotReadableError') {
        setError('Camera is already in use by another app. Please close other apps and try again.');
      } else {
        setError('Unable to access camera. Please try again.');
      }
      setStep('intro');
    }
  };

  // Capture photo with countdown
  const startCapture = () => {
    setCountdown(3);
  };

  // Handle countdown
  useEffect(() => {
    if (countdown === null) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      captureAndCompare();
    }
  }, [countdown]);

  // Capture photo and compare
  const captureAndCompare = useCallback(async () => {
    setCountdown(null);
    setStep('capturing');
    
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not ready. Please try again.');
      setStep('camera');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas (mirror for selfie view)
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0);
    context.setTransform(1, 0, 0, 1, 0, 0);

    // Get base64 image
    const capturedImage = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];

    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setStep('comparing');

    try {
      const response = await api.post('/api/kyc/face/compare', {
        verificationId: userData?.verificationId,
        capturedImage
      });

      if (response.data?.success && response.data?.matched) {
        setSimilarity(response.data.similarity);
        setStep('success');
      } else {
        setSimilarity(response.data?.similarity);
        setError(response.data?.message || 'Face match failed. Please try again.');
        setStep('failed');
      }
    } catch (err) {
      console.error('Face compare error:', err);
      setError(err?.response?.data?.message || 'Face verification failed. Please try again.');
      setStep('failed');
    }
  }, [userData?.verificationId]);

  // Retry face match
  const handleRetry = () => {
    setError('');
    setSimilarity(null);
    setCameraReady(false);
    startCamera();
  };

  // Intro screen
  if (step === 'intro') {
    return (
      <div className="ilp-screen gray-bg">
        <div className="ilp-header white-bg">
          <button className="ilp-back-btn" onClick={() => navigateTo(Screen.ApplicantDetails)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Face Verification</h2>
          <div className="ilp-header-spacer"></div>
        </div>

        <div className="ilp-face-content">
          <div className="ilp-step-indicator">
            <div className="ilp-step-bars">
              <div className="ilp-step-bar filled"></div>
              <div className="ilp-step-bar filled"></div>
              <div className="ilp-step-bar filled"></div>
              <div className="ilp-step-bar"></div>
              <div className="ilp-step-bar"></div>
            </div>
            <p>Step 3 of 5</p>
          </div>

          <div className="ilp-face-intro">
            <div className="ilp-face-icon-large">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                <path d="M9 10h.01M15 10h.01M12 14a2 2 0 002-2v-1a2 2 0 00-4 0v1a2 2 0 002 2zM9 16h6M12 3c4.97 0 9 4.03 9 9s-4.03 9-9 9-9-4.03-9-9 4.03-9 9-9z" />
              </svg>
            </div>
            <h1>Verify Your Identity</h1>
            <p>We need to match your face with your Aadhaar photo to ensure security and prevent fraud.</p>
          </div>

          {error && (
            <div className="ilp-error-message" style={{ marginTop: 12 }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="ilp-face-instructions">
            <div className="ilp-instruction-item">
              <div className="ilp-instruction-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              </div>
              <span>Find good lighting</span>
            </div>
            <div className="ilp-instruction-item">
              <div className="ilp-instruction-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" />
                  <circle cx="12" cy="8" r="2" />
                </svg>
              </div>
              <span>Position face in the frame</span>
            </div>
            <div className="ilp-instruction-item">
              <div className="ilp-instruction-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9.5 14.5L3 21M14.5 9.5L21 3M14.5 9.5L18.5 5.5M9.5 14.5L5.5 18.5" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <span>Keep still during capture</span>
            </div>
          </div>

          {userData?.photoUrl && (
            <div className="ilp-aadhaar-reference">
              <p>Your Aadhaar Photo</p>
              <img src={userData.photoUrl} alt="Aadhaar" />
            </div>
          )}
        </div>

        <div className="ilp-face-footer">
          <button className="ilp-primary-btn full" onClick={startCamera}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Start Camera
          </button>
        </div>
      </div>
    );
  }

  // Camera screen
  if (step === 'camera' || step === 'capturing') {
    return (
      <div className="ilp-screen dark-bg">
        <div className="ilp-header transparent">
          <button className="ilp-back-btn light" onClick={() => {
            if (streamRef.current) {
              streamRef.current.getTracks().forEach(track => track.stop());
            }
            setStep('intro');
            setCameraReady(false);
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Position Your Face</h2>
          <div className="ilp-header-spacer"></div>
        </div>

        <div className="ilp-camera-container">
          <div className="ilp-camera-frame">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              className="ilp-camera-video"
            />
            <div className="ilp-face-outline">
              <svg viewBox="0 0 200 260" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="3">
                <ellipse cx="100" cy="130" rx="80" ry="100" />
              </svg>
            </div>
            {countdown !== null && (
              <div className="ilp-countdown">
                <span>{countdown}</span>
              </div>
            )}
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        <div className="ilp-camera-footer">
          <p className="ilp-camera-hint">
            {cameraReady 
              ? 'Align your face within the oval and tap capture'
              : 'Initializing camera...'}
          </p>
          <button 
            className={`ilp-capture-btn ${!cameraReady || countdown !== null ? 'disabled' : ''}`}
            disabled={!cameraReady || countdown !== null}
            onClick={startCapture}
          >
            <div className="ilp-capture-ring">
              <div className="ilp-capture-inner"></div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Comparing screen
  if (step === 'comparing') {
    return (
      <div className="ilp-screen">
        <div className="ilp-comparing-content">
          <div className="ilp-comparing-animation">
            <div className="ilp-face-scan">
              <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                <circle cx="12" cy="10" r="3" />
                <path d="M12 13c-3.866 0-7 1.79-7 4v2h14v-2c0-2.21-3.134-4-7-4z" />
                <path d="M3 8V5a2 2 0 012-2h3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M21 16v3a2 2 0 01-2 2h-3" />
              </svg>
              <div className="ilp-scan-line"></div>
            </div>
          </div>
          <h2>Verifying Your Identity</h2>
          <p>Comparing your face with Aadhaar photo...</p>
          <div className="ilp-progress-bar">
            <div className="ilp-progress-fill animate"></div>
          </div>
        </div>
      </div>
    );
  }

  // Success screen
  if (step === 'success') {
    return (
      <div className="ilp-screen">
        <div className="ilp-result-content">
          <div className="ilp-result-icon success">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1>Face Verified!</h1>
          <p>Your identity has been successfully verified.</p>
          
          {similarity && (
            <div className="ilp-similarity-badge">
              <span className="ilp-similarity-value">{Math.round(similarity)}%</span>
              <span className="ilp-similarity-label">Match Confidence</span>
            </div>
          )}

          <div className="ilp-verified-info">
            <div className="ilp-verified-row">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Live face detected</span>
            </div>
            <div className="ilp-verified-row">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Matched with Aadhaar photo</span>
            </div>
            <div className="ilp-verified-row">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Identity confirmed</span>
            </div>
          </div>
        </div>

        <div className="ilp-result-footer">
          <button className="ilp-primary-btn full" onClick={() => navigateTo(Screen.PurposeOfVisit)}>
            Continue to Purpose of Visit
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Failed screen
  if (step === 'failed') {
    return (
      <div className="ilp-screen">
        <div className="ilp-result-content">
          <div className="ilp-result-icon failed">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1>Verification Failed</h1>
          <p>{error || 'We could not match your face with your Aadhaar photo.'}</p>
          
          {similarity && (
            <div className="ilp-similarity-badge failed">
              <span className="ilp-similarity-value">{Math.round(similarity)}%</span>
              <span className="ilp-similarity-label">Match Score (Min 80% required)</span>
            </div>
          )}

          <div className="ilp-tips-card">
            <h4>Tips for successful verification:</h4>
            <ul>
              <li>Ensure good lighting on your face</li>
              <li>Remove glasses, hats, or face coverings</li>
              <li>Look directly at the camera</li>
              <li>Keep your face centered in the frame</li>
            </ul>
          </div>
        </div>

        <div className="ilp-result-footer">
          <button className="ilp-primary-btn full" onClick={handleRetry}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 4v6h6M23 20v-6h-6" />
              <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
            </svg>
            Try Again
          </button>
          <button className="ilp-secondary-btn" onClick={() => navigateTo(Screen.VerificationMethod)}>
            Use Different Verification Method
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default FaceMatchScreen;

